import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CalendarioEvento } from '@/types';
import logoSrc from '@/assets/imgs/kora-icon.png';
import { saveOrShareBlob } from './nativeFile';

// ─── Paleta ────────────────────────────────────────────────────────────────
const C = {
  dourado:      [192, 153, 73]  as [number, number, number], // RMBS Gold #C09949
  douradoClaro: [250, 240, 224] as [number, number, number],
  branco:      [255, 255, 255] as [number, number, number],
  metaBg:      [248, 250, 252] as [number, number, number],
  borda:       [203, 213, 225] as [number, number, number],
  textoPrinc:  [30,  41,  59]  as [number, number, number],
  textoSec:    [71,  85,  105] as [number, number, number],
  textoFaint:  [100, 116, 139] as [number, number, number],
  verde:       [22,  163, 74]  as [number, number, number],
  ambar:       [217, 119, 6]   as [number, number, number],
  vermelho:    [220, 38,  38]  as [number, number, number],
};

const STATUS_LABELS: Record<string, string> = {
  confirmado: 'Confirmado',
  pendente:   'Pendente',
  cancelado:  'Cancelado',
};

const PRIORIDADE_LABELS: Record<string, string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta:  'Alta',
};

// ─── Helpers ───────────────────────────────────────────────────────────────
async function carregarLogo(): Promise<string | null> {
  try {
    const res  = await fetch(logoSrc);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function formatarDataHora(ev: CalendarioEvento): string {
  const d = new Date(ev.data_inicio);
  if (Number.isNaN(d.getTime())) return '';
  // Eventos "dia inteiro" são salvos como meia-noite UTC do dia (ver `toDateOnly` em
  // EventoModal.tsx) — usar getters locais aqui voltaria 1 dia em fuso negativo (Brasil, UTC-3).
  if (ev.dia_inteiro) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  }
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function temEntrega(ev: CalendarioEvento): boolean {
  return !!(ev.fornecedor || ev.numero_nfe || ev.peso_veiculo != null || ev.volume != null);
}

function fmtNum(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

// ─── Cabeçalho (página 1 = completo, demais = mini) ────────────────────────
function drawHeader(
  doc: jsPDF,
  logo: string | null,
  titulo: string,
  subtitulo: string | undefined,
  empresaNome: string | undefined,
  geradoEm: string,
  primeiraPagina: boolean,
): void {
  const pw = doc.internal.pageSize.width;

  if (primeiraPagina) {
    // banda dourada
    doc.setFillColor(...C.dourado);
    doc.rect(0, 0, pw, 40, 'F');

    // ícone kora (quadrado, 1:1)
    if (logo) doc.addImage(logo, 'PNG', 10, 6, 18, 18);

    // "kora" abaixo do ícone
    doc.setTextColor(...C.textoPrinc);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text('kora', 10, 30);

    // título do relatório (direita)
    doc.setFontSize(17);
    doc.setFont('helvetica', 'bold');
    doc.text(titulo, pw - 14, 17, { align: 'right' });

    // subtítulo (direita)
    if (subtitulo) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.text(subtitulo, pw - 14, 27, { align: 'right' });
    }

    // banda de metadados
    doc.setFillColor(...C.metaBg);
    doc.rect(0, 40, pw, 13, 'F');
    doc.setDrawColor(...C.borda);
    doc.setLineWidth(0.3);
    doc.line(0, 40, pw, 40);
    doc.line(0, 53, pw, 53);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.textoSec);

    const partes: string[] = [];
    if (empresaNome) partes.push(`Empresa: ${empresaNome}`);
    partes.push(`Gerado em: ${geradoEm}`);
    doc.text(partes.join('   ·   '), 14, 48.5);
  } else {
    // mini-cabeçalho em páginas subsequentes
    doc.setFillColor(...C.dourado);
    doc.rect(0, 0, pw, 16, 'F');
    if (logo) doc.addImage(logo, 'PNG', 10, 3, 10, 10);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.textoPrinc);
    doc.text(titulo, pw - 14, 10, { align: 'right' });
  }
}

function drawFooter(doc: jsPDF, pagina: number, total: number, geradoEm: string): void {
  const pw = doc.internal.pageSize.width;
  const ph = doc.internal.pageSize.height;
  doc.setDrawColor(...C.borda);
  doc.setLineWidth(0.3);
  doc.line(14, ph - 13, pw - 14, ph - 13);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.textoFaint);
  doc.text('kora',              14,      ph - 8);
  doc.text(`Página ${pagina} de ${total}`, pw / 2, ph - 8, { align: 'center' });
  doc.text(`Gerado em: ${geradoEm}`, pw - 14, ph - 8, { align: 'right' });
}

// ─── Exportação principal ───────────────────────────────────────────────────
export async function exportAgendaPdf(
  eventos: CalendarioEvento[],
  options: { titulo: string; subtitulo?: string; empresaNome?: string },
): Promise<void> {
  const logo      = await carregarLogo();
  const geradoEm  = new Date().toLocaleString('pt-BR');
  const ordenados = [...eventos].sort(
    (a, b) => new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime(),
  );
  const comEntrega = ordenados.some(temEntrega);

  const headers = comEntrega
    ? ['Data', 'Título', 'Calendário', 'Fornecedor', 'NFe', 'Peso (kg)', 'Volume (m³)', 'Status']
    : ['Data', 'Título', 'Calendário', 'Status', 'Prioridade', 'Local'];

  const body = ordenados.map((ev) =>
    comEntrega
      ? [
          formatarDataHora(ev),
          ev.titulo,
          ev.calendario?.nome ?? '',
          ev.fornecedor   || '—',
          ev.numero_nfe   || '—',
          fmtNum(ev.peso_veiculo),
          fmtNum(ev.volume),
          STATUS_LABELS[ev.status] ?? ev.status,
        ]
      : [
          formatarDataHora(ev),
          ev.titulo,
          ev.calendario?.nome ?? '',
          STATUS_LABELS[ev.status]    ?? ev.status,
          PRIORIDADE_LABELS[ev.prioridade] ?? ev.prioridade,
          ev.local ?? '',
        ],
  );

  const doc = new jsPDF(comEntrega ? 'landscape' : 'portrait');

  // cabeçalho da página 1
  drawHeader(doc, logo, options.titulo, options.subtitulo, options.empresaNome, geradoEm, true);

  autoTable(doc, {
    head:    [headers],
    body,
    startY:  57,          // abaixo do cabeçalho completo
    margin:  { top: 20, left: 14, right: 14, bottom: 20 },
    styles: {
      fontSize:    8.5,
      cellPadding: { top: 3.5, right: 4, bottom: 3.5, left: 4 },
      textColor:   C.textoPrinc,
      lineColor:   C.borda,
      lineWidth:   0.2,
      font:        'helvetica',
    },
    headStyles: {
      fillColor:  C.dourado,
      textColor:  C.textoPrinc,
      fontStyle:  'bold',
      fontSize:   8.5,
      halign:     'left',
    },
    alternateRowStyles: {
      fillColor: C.douradoClaro,
    },
    columnStyles: comEntrega
      ? {
          0: { cellWidth: 30 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 38 },
          3: { cellWidth: 38 },
          4: { cellWidth: 28 },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 24 },
        }
      : {
          0: { cellWidth: 32 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 38 },
          3: { cellWidth: 24 },
          4: { cellWidth: 20 },
          5: { cellWidth: 'auto' },
        },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const ev = ordenados[data.row.index];
      if (!ev) return;

      // colorir coluna Status
      const colStatus = comEntrega ? 7 : 3;
      if (data.column.index === colStatus) {
        if (ev.status === 'confirmado') data.cell.styles.textColor = C.verde;
        else if (ev.status === 'pendente') data.cell.styles.textColor = C.ambar;
        else if (ev.status === 'cancelado') data.cell.styles.textColor = C.vermelho;
      }

      // colorir coluna Prioridade (calendário padrão)
      if (!comEntrega && data.column.index === 4) {
        if (ev.prioridade === 'alta') data.cell.styles.textColor = C.vermelho;
        else if (ev.prioridade === 'media') data.cell.styles.textColor = C.ambar;
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawHeader(doc, logo, options.titulo, options.subtitulo, options.empresaNome, geradoEm, false);
      }
    },
  });

  // rodapé em todas as páginas (requer page count final)
  const totalPaginas = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPaginas; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPaginas, geradoEm);
  }

  const blob = doc.output('blob');
  await saveOrShareBlob(blob, `agenda_${new Date().toISOString().split('T')[0]}.pdf`);
}
